import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';

interface SyncState {
  lastUpdate?: number,
  state?: string//unknown, expired, uptodate
}

@Injectable({
  providedIn: 'root'
})

/**
 * 
* Sync state service keep the record of the last update for each database
* for each "cold" collection [zone, unit, tracker, model, chart, dashboard, flow, intervention]
* "hot" [track, event, metric]
* 
*/
export class SyncStateService {

  /**
   * Sync state look like this:
   * {
   *    [dbNameX]:{
   *      [collectionX]:{
   *          lastUpdate:number,
   *          expired:boolean
   *      },
   *      ...
   *    },
   *    ...
   * }
   */
  private syncedCollections: string[] = [];
  private syncState: Record<string, Record<string, SyncState>> = {};

  constructor(
    private localStorageService: LocalStorageService,
    private apiService: ApiService
  ) {

    let localSyncState: any = this.localStorageService.getItem("syncState");
    if (localSyncState) {
      this.syncState = localSyncState;
      Object.keys(this.syncState).forEach(dbName => {
        const dbState = this.syncState[dbName];
        if (dbState) {
          Object.keys(dbState).forEach(collection => {
            if (dbState[collection]) {
              dbState[collection].state = "unknown";
            }
          })
        }
      })
    }
  }

  // Get local sync state or
  getSyncState(dbName: string): Observable<any> {
    if (Object.keys(this.syncState).includes(dbName)) {
      return of(this.syncState[dbName])
    } else {
      return this.getDbSyncStateFromServer(dbName)
    }
  }

  // Get local collection sync state or
  getCollectionSyncState(dbName: string, collection: string): Observable<any> {
    if (this.syncState[dbName] && this.syncState[dbName][collection]?.state !== "unknown") {
      return of(this.syncState[dbName])
    } else {
      return this.getDbSyncStateFromServer(dbName)
    }
  }
  /**
     * Delete the local sync state
     * @param dbName 
     */
  deleteSyncState(dbName: string): void {
    if (this.syncState[dbName]) {
      delete this.syncState[dbName];
      this.localStorageService.setItem("syncState", this.syncState);
    }
  }
  /**
   * Update the local sync state
   * @param dbName 
   * @param collectionName 
   * @param lastUpdate 
   * @param state 
   */
  updateSyncState(dbName: string, collectionName: string, lastUpdate: number, state: string): void {
    if (!this.syncState[dbName]) {
      this.syncState[dbName] = {};
    }

    if (!this.syncedCollections.includes(collectionName)) {
      this.localStorageService.removeItem(dbName + "_" + collectionName);
      return;
    }

    if (!this.syncState[dbName][collectionName]) {
      this.syncState[dbName][collectionName] = {};
    }

    this.syncState[dbName][collectionName].state = state;
    this.syncState[dbName][collectionName].lastUpdate = lastUpdate;

    this.localStorageService.setItem("syncState", this.syncState);
  }

  /**
   * Check expiration
   * @param dbName 
   * @param collectionName 
   * @param lastUpdateFromServer 
   * @returns is expired ?
   */
  getCollectionState(dbName: string, collectionName: string, lastUpdateFromServer: number): string {
    const localState = this.syncState[dbName]?.[collectionName];

    if (!localState || !localState.lastUpdate) {
      return "expired";
    }

    return lastUpdateFromServer > localState.lastUpdate ? "expired" : "uptodate";
  }

  /**
   * Get last sync state from server and update the local one
   * @param dbName 
   * @param checkExpiration 
   * @returns 
   */
  getDbSyncStateFromServer(dbName: string): Observable<any> {
    return this.apiService.getSyncState(dbName)
      .pipe(
        map(serverSyncState => {
          if (!this.syncedCollections.length)
            this.syncedCollections = Object.keys(serverSyncState);

          Object.keys(serverSyncState).forEach(c => {
            this.updateSyncState(dbName, c, serverSyncState[c], this.getCollectionState(dbName, c, serverSyncState[c]));
          });

          this.localStorageService.setItem("syncState", this.syncState);
          return this.syncState[dbName]
        })
      )
  }
}
